// @flow

import {PaginationStripe} from '@pb/kit-component-pagination';

import {HighlightType, TableSize} from './constants';
import {Cell} from './data-table-cell';
import {Row} from './data-table-row';
import {DataTableRowPlaceholder} from './data-table-row-placeholder';
import {DataTableHover} from './data-table-hover';

import type {Element, Node} from 'react';
import type {SortablePosition} from '@pb/kit-component-native-sortable';
// $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
import type {UseOverlayScrollbarsInstance} from 'overlayscrollbars-react';
import type {TableSizeType} from './constants';


export type HeadersType = ({ column: Column<{||}> }, cellOptions: HeaderCellOptions<{||}>) => Element<typeof Cell>;

export type RowsRenderType = (RowsParams<{||}>, CellOptions<{||}>) => Element<typeof Cell>;

export type RowType<DataRow = {||}> = RowData<DataRow> | TreeRowData<DataRow>;

export type TargetElementType = {|
    dataId: string,
    sortablePosition: SortablePosition,
|};

export type SortableItemEvent = {|
    oldIndex: number,
    newIndex: number,
    type: string,
|};

export type RenderCustomArrows = () => React$Node;

export type TreeDataObj<DataRow = {||}> = {|
    ...DataRow,
    treeId: number | string,
    level: number,
    id: number | string,
    isFlat: boolean,
    expanded?: boolean,
    hideCheckbox?: boolean,
    isTotal?: boolean,
    label?: string,
|};

export type Column<DataColumn = {||}> = {|
    ...DataColumn,
    columnId: string,
    fixed: boolean,
    resizable?: boolean,
    defaultWidth: number,
    width?: number,
    minWidth?: number,
    maxWidth?: number,
|};

export type RowData<DataRow = {||}> = {|
    ...DataRow,
    id: number | string,
    mainRow?: boolean,
    highlightType?: $Keys<typeof HighlightType>,
    rowHeight?: number,
    headerRow?: boolean,
    topTableRow?: boolean,
    columnValues: {
        [key: string]: any,
    },
|};

export type TreeRowData<DataRow = {||}> = {|
    ...DataRow,
    columnValues: {
        [key: string]: any,
    },
    id: number | string,
    level: number,
    expanded?: boolean,
    isFlat?: boolean,
    isTotal?: boolean,
    parentId?: string,
    children?: Array<TreeRowData<DataRow>>,
    rowsWithChildren?: boolean,
    rowHeight?: number,
    label?: string,
    mainTotal?: boolean,
    headerRow?: boolean,
    mainRow?: boolean,
    highlightType?: $Keys<typeof HighlightType>,
    additionalRows?: Array<TreeRowData<DataRow>>,
|};

export type DataTableResizeState = {|
    width: number,
    deltaX: number,
    resize: boolean | null,
|};

export type WidthAuto = 'auto';

export type VirtualizeHorizontalData = {|
    cellStartIndex: number,
    cellEndIndex: number,
    leftOffset: number,
    rightOffset: number | WidthAuto,
    isVirtualizedCells: boolean,
    renderCellPlaceholder?: boolean,
|};

export type RenderTableType<DataColumn = {||}> = {|
    columns: Array<Column<DataColumn>>,
    width: number,
    fixed?: boolean,
    virtualizeHorizontalData: VirtualizeHorizontalData,
|};

export type RenderHeaderType<DataColumn = {||}> = {|
    columns: Array<Column<DataColumn>>,
    width: number,
    fixed?: boolean,
    virtualizeHorizontalData: VirtualizeHorizontalData,
|};

export type PropertyClasses = {|
    fixed?: boolean,
    pinned?: boolean,
    topTable?: boolean,
|};

export type HeaderCellOptions<DataColumn = {||}> = {|
    id: string,
    key: string,
    headerCell: boolean,
    index: number,
    isLastChild: boolean,
    resizable: boolean,
    onStartResize: (column: Column<DataColumn>, key: number) => void,
    onResize: (state: DataTableResizeState, column: Column<DataColumn>, key: number) => void,
    onStopResize: (state: DataTableResizeState, column: Column<DataColumn>, key: number) => void,
    tableId?: string,
|};

export type CellOptions<DataColumn = {||}> = {|
    treeDataObj?: TreeDataObj<{||}>,
    id: string,
    key: string,
    index: number,
    isLastChild: boolean,
    resizable: boolean,
    setHover?: () => void,
    removeHover?: () => void,
    headerCell?: boolean,
    column?: Column<DataColumn>,
    onStartResize: (column: Column<DataColumn>, key: number) => void,
    onResize: (state: DataTableResizeState, column: Column<DataColumn>, key: number) => void,
    onStopResize: (state: DataTableResizeState, column: Column<DataColumn>, key: number) => void,
    allowedDragArea?: boolean,
|};

export type RowsMapType = {|
    rowsHeight: Array<number>,
    rowsOffset: Array<number>,
    tableHeight: number,
|};

export type SplitByPinnedRowsData<DataRow = {||}> = {|
    pinnedRows: Array<RowData<DataRow>>,
    ordinaryRows: Array<RowData<DataRow>>,
|};

export type RowsParams<DataRow = {||}, DataColumn = {||}> = {|
    column: Column<DataColumn>,
    row: RowData<DataRow>,
    rowIndex: number,
    pinned: boolean,
    isLastChild: boolean,
    fixedRow: boolean,
|};

export type RowsTreeParams<DataRow = {||}, DataColumn = {||}> = {|
    ...RowsParams<DataRow, DataColumn>,
    row: TreeRowData<DataRow>,
|};

export type TableRowsProps<DataRow = {||}, DataColumn = {||}> = {|
    ...Data<DataRow, DataColumn>,
    indexOffset: number,
    width: number,
    tableSize: $Keys<typeof TableSize>,
    fixed: boolean,
    pinned?: boolean,
    topTable?: boolean,
    virtualizeHorizontalData: VirtualizeHorizontalData,
    rowsVirtualization?: boolean,
    tableBodyMix?: string,
|};

export type ColumnGroup<T> = {|
    fixed: T,
    others: T,
|};

export type RenderHeaders<DataColumn = {||}> = (
    {| column: Column<DataColumn> |},
    cellOptions: HeaderCellOptions<DataColumn>
) => Element<typeof Cell>;

export type RenderRows<DataRow = {||}, DataColumn = {||}> = (
    RowsParams<DataRow, DataColumn>,
    CellOptions<DataColumn>
) => Element<typeof Cell>;

export type RenderRowsTree<DataRow = {||}, DataColumn = {||}> = (
    RowsTreeParams<DataRow, DataColumn>,
    CellOptions<DataColumn>
) => Element<typeof Cell>;

export type VirtualizedRow = Element<typeof Row | typeof DataTableRowPlaceholder>;

export type StopResizeParams<DataColumn = {||}> = {| ...Column<DataColumn>, width: number |};

export type RenderRowsParams<DataRow = {||}, DataColumn = {||}> = {|
    indexOffset: number,
    columns: Array<Column<DataColumn>>,
    rows: Array<RowData<DataRow>>,
    fixed: boolean,
    pinned: boolean,
    virtualizeHorizontalData: VirtualizeHorizontalData,
    rowsVirtualization: boolean,
    renderRows: RenderRows<DataRow, DataColumn>,
|};

export type RenderTreeRowsStructureParams<DataRow = {||}, DataColumn = {||}> = {|
    columns: Array<Column<DataColumn>>,
    rows: Array<Array<TreeRowData<DataRow>>>,
    fixed: boolean,
    virtualizeHorizontalData: VirtualizeHorizontalData,
    rowsVirtualization: boolean,
    renderTreeRows: RenderRowsTree<DataRow, DataColumn>,
|};

export type RenderTreeRowsParams<DataRow = {||}, DataColumn = {||}> = {|
    columns: Array<Column<DataColumn>>,
    row: TreeRowData<DataRow>,
    rowIndex: number,
    fixed: boolean,
    treeDataObj?: TreeDataObj<{||}>,
    pinned?: boolean,
    virtualizeHorizontalData: VirtualizeHorizontalData,
    rowsVirtualization: boolean,
    renderTreeRows: RenderRowsTree<DataRow, DataColumn>,
|};

export type Data<DataRow = {||}, DataColumn = {||}> = {|
    columns: Array<Column<DataColumn>>,
    rows?: Array<RowData<DataRow>>,
    treeRows?: Array<TreeRowData<DataRow>>,
|};

export type RowPlaceholderParams = {|
    rowHeight: number,
    rowId: string,
    rowsVirtualization: boolean,
    rowIndex: number,
    fixed: boolean,
|};

export type RowHoverParams = {|
    headerRow?: boolean,
    fixed: boolean,
    pinned: boolean,
    disabledHover?: boolean,
    id: number | string,
    setRowHover: (rowIndex: number | string, rowTypes: PropertyClasses) => void,
    removeRowHover: (rowIndex: number | string, rowTypes: PropertyClasses) => void,
|};

export type DataTableProps<DataRow = {||}, DataColumn = {||}> = {|
    /**
     * Object with data of columns, rows and treeRows
     */
    data: Data<DataRow, DataColumn>,
    /**
     * Render header
     */
    headers: RenderHeaders<DataColumn>,
    /**
     * Element that wraps Data Table (when you need to use events inside element but not on window/document)
     */
    wrapperElement?: HTMLElement | null,
    /**
     * Render rows
     */
    renderRows?: RenderRows<DataRow, DataColumn>,
    /**
     * Render tree rows
     */
    renderRowsTree?: RenderRowsTree<DataRow, DataColumn>,
    /**
     * PaginationStripe component from @pb/kit-component-pagination
     */
    paginationStripe?: Element<typeof PaginationStripe>,
    /**
     * Reordering functionality
     */
    reordering?: {|
        disabled: boolean,
        onChange: (columns: Array<string>, movableItemId: string, event: SortableItemEvent) => void,
        removable?: boolean,
    |},
    /**
     * Get dataTable ref
     */
    getRefTable?: (table: HTMLElement) => void,
    /**
     * Add custom navigation
     */
    customNavigation?: Node,
    /**
     * Set top offset header for pinned header position on scroll. Use only if `stickyReferenceElement` doesn’t work for your case
     */
    getHeaderTopOffset?: () => number,
    /**
     * Set event listener type to listen a container event
     */
    containerEventListenersType: string,
    /**
     * Callback on resize scrollable container (right area)
     */
    onScrollableSizeChange?: (containerSize: number) => void,
    /**
     * Render custom arrows for scrolling
     */
    renderCustomArrows?: RenderCustomArrows,
    /**
     * Callback to set position of scroll when mounting component
     */
    setScrollablePosition?: (callback: (scrollRatio: number) => void) => void,
    /**
     * Element for calculating offset top for a sticky header. Use this instead of `getHeaderTopOffset` in most cases to get the default behaviour
     */
    stickyReferenceElement?: HTMLElement,
    /**
     * Set table message
     */
    tableMessage?: string,
    /**
     * Deaseble hover of row
     */
    disabledHover?: boolean,
    /**
     * Set anchor for getting offset header for pinned header position on scroll in modal
     */
    scrollableRef?: HTMLDivElement,
    /**
     * Get hover instance for controlling hover (e.g. when opening a modal)
     */
    getHoverInstance?: (hover: DataTableHover) => void,
    /**
     * Prevent table scrolling
     */
    disabledScroll?: boolean,
|};

export type DefaultProps<DataColumn = {||}> = $Shape<{|
    id?: string,
    mix?: string,
    tableSize?: TableSizeType,
    cellsVirtualization?: boolean,
    minCellsForVirtualization?: number,
    minRowsForVirtualization?: number,
    modal?: boolean,
    onStopResize?: (column: StopResizeParams<DataColumn>) => void,
    overscanCellsRatio?: number,
    overscanRows?: number,
    rowsVirtualization?: boolean,
    /**
     * @deprecated It will be removed in 6 months 12-03-2025. Use scrollRef instead.
     * */
    scrollToCell?: (cellOffsets: Array<number>, TableCanvasWidth: number, tableViewport: number) => void,
    /**
     * Ref to scrollable instance.
     * @example
     * ```tsx
     * const scrollRef = useRef<HTMLElement>(null);
     *
     * const scrollToCell = () => {
     *  scrollRef.current?.().elements().viewport.scrollTo({ left: 100 });
     * };
     *
     * <DataTable
     *   scrollRef={scrollRef}
     *   />
     *   ```
     */
    scrollRef?: React$Ref<UseOverlayScrollbarsInstance>,
    showNoDataMsg?: boolean,
    viewportHeight?: number,
|}>;

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./data-table-cell");

require("./constants");

require("./data-table-hover");

require("./data-table-row");

require("./data-table-row-placeholder");
